import CustomerForm from "@/pages/customers/CustomerForm";
import Customers from "@/pages/customers/Customers";
import Expenses from "@/pages/expenses/Expenses";
import ExpenseForm from "@/pages/expenses/ExpensesForm";
import FastOrderForm from "@/pages/fastOrders/FastOrderForm";
import FastOrders from "@/pages/fastOrders/FastOrders";
import OrderForm from "@/pages/orders/OrderForm";
import { Orders } from "@/pages/orders/Orders";
import ProductForm from "@/pages/products/ProductForm";
import Products from "@/pages/products/Products";
import Offers from "@/pages/offers/Offers";
import OfferForm from "@/pages/offers/OfferForm";
import TagForm from "@/pages/tags/TagForm";
import { Routes, Route, Navigate } from "react-router-dom";
import { DynamicBreadcrumb } from "./DynamicBreadCrumb";
import { SidebarInset, SidebarTrigger } from "./ui/sidebar";
import { Separator } from "./ui/separator";
import Tags from "@/pages/tags/Tags";
import ProtectedRoute from "./ProtectedRoute";
import { Dashboard } from "@/pages/dashboard/Dashboard";
import { UserProfile } from "@/pages/users/UserProfile";
import { DelinquencyReport } from "@/pages/dashboard/DelinquencyReport";
import { DeliveryCalendar } from "@/pages/dashboard/DeliveryCalendar";
import { FastOrderDetails } from "@/pages/fastOrders/FastOrderDetails";
import { OrderDetails } from "@/pages/orders/OrderDetails";
import Sentry from "@/sentry";
import ErrorFallback from "./ErrorFallback";
import Help from "@/pages/help/Help";
import Home from "@/pages/home/Home";
import { useAppManager } from "@/context/AppManagerContext";
import { usePermissions } from "@/hooks/use-permissions";
import { NotFound } from "./NotFound";
import Menus from "@/pages/menu/Menus";
import MenusForm from "@/pages/menu/MenusForm";
import OrganizationForm from "@/pages/organizations/OrganizationsForm";
import OrganizationDetails from "@/pages/organizations/OrganizationDetails";
import OrganizationMembers from "@/pages/organizations/OrganizationMembers";
import PreOrder from "@/pages/preOrder/PreOrder";
import PreOrderFlow from "@/pages/preOrder/PreOrderFlow";

export const AppRoutes = () => {
  const { currentRole, currentPlan } = useAppManager();
  const { canAccessDashboard, canAccessDelinquency, canAccessCalendar } =
    usePermissions({
      userRole: currentRole,
      planFeatures: currentPlan?.features ?? [],
    });
  return (
    <SidebarInset className="flex flex-col min-h-screen">
      <header className="flex h-16 items-center gap-4 border-b bg-background px-6">
        <SidebarTrigger />
        <Separator orientation="vertical" className="h-6" />
        <DynamicBreadcrumb />
      </header>
      <main className="flex-1 overflow-auto">
        <Sentry.ErrorBoundary
          fallback={({ error, resetError }) => {
            const isError = error instanceof Error;
            return (
              <ErrorFallback
                error={isError ? error : new Error(String(error))}
                resetError={resetError}
              />
            );
          }}
        >
          <Routes>
            {/* Rota inicial */}
            <Route path="/" element={<Navigate to="/inicio" replace />} />

            {/* Rotas protegidas */}
            <Route element={<ProtectedRoute />}>
              <Route path="/inicio" element={<Home />} />

              <Route
                path="/organizacoes/:id/editar"
                element={<OrganizationForm />}
              />

              <Route
                path="/organizacoes/:id/membros"
                element={<OrganizationMembers />}
              />

              <Route
                path="/organizacoes/:id"
                element={<OrganizationDetails />}
              />

              <Route path="/perfil" element={<UserProfile />} />

              {canAccessDashboard && (
                <Route path="/dashboard" element={<Dashboard />} />
              )}
              {canAccessDelinquency && (
                <Route path="/inadimplentes" element={<DelinquencyReport />} />
              )}
              {canAccessCalendar && (
                <Route path="/entregas" element={<DeliveryCalendar />} />
              )}

              <Route path="/pedidos" element={<Orders />} />
              <Route path="/pedidos/novo" element={<OrderForm />} />
              <Route path="/pedidos/:id/" element={<OrderDetails />} />
              <Route path="/pedidos/:id/editar" element={<OrderForm />} />

              <Route path="/pedidos-rapidos" element={<FastOrders />} />
              <Route path="/pedidos-rapidos/novo" element={<FastOrderForm />} />
              <Route
                path="/pedidos-rapidos/:id/editar"
                element={<FastOrderForm />}
              />
              <Route
                path="/pedidos-rapidos/:id/"
                element={<FastOrderDetails />}
              />

              <Route path="/clientes" element={<Customers />} />
              <Route path="/clientes/novo" element={<CustomerForm />} />
              <Route path="/clientes/:id/editar" element={<CustomerForm />} />

              <Route path="/produtos" element={<Products />} />
              <Route path="/produtos/novo" element={<ProductForm />} />
              <Route path="/produtos/:id/editar" element={<ProductForm />} />

              <Route path="/ofertas" element={<Offers />} />
              <Route path="/ofertas/novo" element={<OfferForm />} />
              <Route path="/ofertas/:id/editar" element={<OfferForm />} />

              <Route path="/tags" element={<Tags />} />
              <Route path="/tags/novo" element={<TagForm />} />
              <Route path="/tags/:id/editar" element={<TagForm />} />

              <Route path="/despesas" element={<Expenses />} />
              <Route path="/despesas/novo" element={<ExpenseForm />} />
              <Route path="/despesas/:id/editar" element={<ExpenseForm />} />

              <Route path="/catalogos" element={<Menus />} />
              <Route path="/catalogos/novo" element={<MenusForm />} />
              <Route path="/catalogos/:id/editar" element={<MenusForm />} />

              <Route path="/pre-pedidos" element={<PreOrder />} />
              <Route path="/pre-pedidos/:id" element={<PreOrderFlow />} />

              <Route path="/ajuda" element={<Help />} />

              <Route path="*" element={<NotFound />} />
            </Route>
          </Routes>
        </Sentry.ErrorBoundary>
      </main>
    </SidebarInset>
  );
};
